export type OverlayBox = { x: number; y: number; w: number; h: number };
export type OverlayAlign = {
  x: 'left' | 'center' | 'right';
  y: 'top' | 'center' | 'bottom';
};
export type OverlayMeta = {
  layoutW: number;
  layoutH: number;
  contentW: number;
  contentH: number;
  bbox: OverlayBox;
};

/** Layout canvas contained on the photo. Offset 0,0 is the top-left of the photo. */
export function containOverlayRect(
  srcW: number,
  srcH: number,
  destW: number,
  destH: number,
  scale: number,
  offsetX: number,
  offsetY: number,
) {
  const srcRatio = srcW / Math.max(1, srcH);
  const destRatio = destW / destH;
  let dw: number;
  let dh: number;
  if (srcRatio > destRatio) {
    dw = destW * scale;
    dh = dw / srcRatio;
  } else {
    dh = destH * scale;
    dw = dh * srcRatio;
  }
  return {
    dw,
    dh,
    dx: offsetX * destW,
    dy: offsetY * destH,
  };
}

/**
 * Visible (trimmed) overlay size on the photo, keeping the same visual scale
 * it had on the original 9:16 canvas.
 */
export function overlayDestRect(
  contentW: number,
  contentH: number,
  layoutW: number,
  layoutH: number,
  destW: number,
  destH: number,
  scale: number,
  offsetX: number,
  offsetY: number,
) {
  const fit = containOverlayRect(layoutW, layoutH, destW, destH, scale, 0, 0);
  const dw = (contentW / Math.max(1, layoutW)) * fit.dw;
  const dh = (contentH / Math.max(1, layoutH)) * fit.dh;
  return {
    dw,
    dh,
    dx: offsetX * destW,
    dy: offsetY * destH,
  };
}

export function overlayCssPercent(
  contentW: number,
  contentH: number,
  layoutW: number,
  layoutH: number,
  destW: number,
  destH: number,
  scale: number,
  offsetX: number,
  offsetY: number,
) {
  const { dx, dy, dw, dh } = overlayDestRect(
    contentW,
    contentH,
    layoutW,
    layoutH,
    destW,
    destH,
    scale,
    offsetX,
    offsetY,
  );
  return {
    left: `${(dx / destW) * 100}%`,
    top: `${(dy / destH) * 100}%`,
    width: `${(dw / destW) * 100}%`,
    height: `${(dh / destH) * 100}%`,
  };
}

export function opaqueBoundingBox(
  width: number,
  height: number,
  pixels: ArrayLike<number>,
  alphaMin = 12,
): OverlayBox | null {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (pixels[(y * width + x) * 4 + 3] > alphaMin) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/** Compact filters keep their template corner; full-card filters stay centered. */
export function defaultOverlayAlign(bbox: OverlayBox, layoutW: number, layoutH: number): OverlayAlign {
  const coverage = (bbox.w * bbox.h) / Math.max(1, layoutW * layoutH);
  if (coverage > 0.5) return { x: 'center', y: 'center' };
  const left = bbox.x / layoutW;
  const top = bbox.y / layoutH;
  const right = (bbox.x + bbox.w) / layoutW;
  const bottom = (bbox.y + bbox.h) / layoutH;
  const x: OverlayAlign['x'] = left < 0.22 ? 'left' : right > 0.78 && left > 0.4 ? 'right' : 'center';
  const y: OverlayAlign['y'] = top < 0.22 ? 'top' : bottom > 0.78 && top > 0.4 ? 'bottom' : 'center';
  return { x, y };
}

export function offsetForAlign(align: OverlayAlign, dw: number, dh: number, destW: number, destH: number) {
  const maxX = 1 - dw / Math.max(1, destW);
  const maxY = 1 - dh / Math.max(1, destH);
  const x = align.x === 'left' ? 0 : align.x === 'right' ? maxX : maxX / 2;
  const y = align.y === 'top' ? 0 : align.y === 'bottom' ? maxY : maxY / 2;
  return { x, y };
}

export function clampOverlayOffset(
  offsetX: number,
  offsetY: number,
  contentW: number,
  contentH: number,
  layoutW: number,
  layoutH: number,
  destW: number,
  destH: number,
  scale: number,
) {
  const { dw, dh } = overlayDestRect(contentW, contentH, layoutW, layoutH, destW, destH, scale, 0, 0);
  const minX = Math.min(0, 1 - dw / destW);
  const maxX = Math.max(0, 1 - dw / destW);
  const minY = Math.min(0, 1 - dh / destH);
  const maxY = Math.max(0, 1 - dh / destH);
  return {
    x: Math.max(minX, Math.min(maxX, offsetX)),
    y: Math.max(minY, Math.min(maxY, offsetY)),
  };
}

export function trimTransparentCanvas(canvas: HTMLCanvasElement, padding = 8): OverlayMeta & { canvas: HTMLCanvasElement } {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const fallback: OverlayBox = { x: 0, y: 0, w: canvas.width, h: canvas.height };
  if (!ctx) {
    return { canvas, bbox: fallback, layoutW: canvas.width, layoutH: canvas.height, contentW: canvas.width, contentH: canvas.height };
  }
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const opaque = opaqueBoundingBox(canvas.width, canvas.height, imageData.data) ?? fallback;
  const x = Math.max(0, opaque.x - padding);
  const y = Math.max(0, opaque.y - padding);
  const w = Math.min(canvas.width - x, opaque.w + padding * 2);
  const h = Math.min(canvas.height - y, opaque.h + padding * 2);
  const out = document.createElement('canvas');
  out.width = Math.max(1, w);
  out.height = Math.max(1, h);
  const outCtx = out.getContext('2d');
  outCtx?.drawImage(canvas, x, y, w, h, 0, 0, w, h);
  return {
    canvas: out,
    bbox: opaque,
    layoutW: canvas.width,
    layoutH: canvas.height,
    contentW: out.width,
    contentH: out.height,
  };
}
